import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface TestCase {
  Id: string;
  ExternalId: string;
  GroupId: string;
  Name: string;
  Description: string;
  InputData: Record<string, any>;
  Files?: Record<string, any>;
  ExpectedOutput?: Record<string, any>;
  CategoryId: string;
  Category?: any;
  Group?: any;
}

export interface TestRunData {
  testRunId: string;
  testGroupName: string;
  testCasesCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class TestCaseService {
  private api: AxiosInstance;

  constructor(@Inject(PLATFORM_ID) private platformId: Object, private authService: KeycloakAuthService, private apiConfig: ApiConfigService) {
    this.api = axios.create({
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor to dynamically set baseURL and include OAuth token
    this.api.interceptors.request.use((config) => {
      // Dynamically construct baseURL on each request to get current tenantId
      if (isPlatformBrowser(this.platformId)) {
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/${tenantId}/testing`;
      }

      const token = this.authService.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  async getCases(): Promise<Array<TestCase> | null> {
    try {
      const response = await this.api.get("/case");
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getTestCase(id: string): Promise<TestCase | null> {
    try {
      const response = await this.api.get("/case/" + id);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async updateTestCase(testCase: TestCase): Promise<AxiosResponse | null> {
    try {
      const response = await this.api.put("/case/" + testCase.Id, testCase);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async createTestCase(testCase: TestCase): Promise<string | null> {
    try {
      const response = await this.api.post("/case", testCase);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async deleteTestCase(id: string): Promise<AxiosResponse | null> {
    try {
      const response = await this.api.delete("/case/" + id);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async startTestCase(id: string): Promise<TestRunData | null> {
    try {
      const response = await this.api.post("/case/" + id + "/run");
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }
}
